import React, { useState } from 'react';
import { Button } from '../ui/Button';
import { ChevronDown, FolderOpen, Plus, Loader } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface Project {
  id: string;
  name: string;
  isActive: boolean;
}

interface ProjectSelectorProps {
  projects: Project[];
  isLoading?: boolean;
  selectedProjectId?: string;
  onProjectSelect?: (projectId: string) => void;
}

export function ProjectSelector({ 
  projects, 
  isLoading = false, 
  selectedProjectId,
  onProjectSelect
}: ProjectSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  
  const selectedProject = selectedProjectId 
    ? projects.find(p => p.id === selectedProjectId) 
    : projects[0];

  const handleProjectSelect = (projectId: string) => {
    setIsOpen(false);
    if (onProjectSelect) {
      onProjectSelect(projectId);
    }
  };

  const handleCreateProject = () => {
    setIsOpen(false);
    navigate('/projects');
  };

  if (isLoading) {
    return (
      <Button variant="outline" disabled className="flex items-center space-x-2">
        <Loader className="w-4 h-4 animate-spin" />
        <span>Loading projects...</span>
      </Button>
    );
  }

  if (projects.length === 0) {
    return (
      <Button 
        variant="outline" 
        onClick={handleCreateProject}
        className="flex items-center space-x-2"
      >
        <Plus className="w-4 h-4" />
        <span>Create Project</span>
      </Button>
    );
  }

  return (
    <div className="relative">
      <Button
        variant="outline"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2"
      >
        <FolderOpen className="w-4 h-4" />
        <span className="max-w-32 truncate">{selectedProject?.name || 'Select Project'}</span>
        <ChevronDown className="w-4 h-4" />
      </Button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50">
          <div className="p-2">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 px-2 py-1 mb-1">
              Select Project
            </div>
            {projects.map((project) => (
              <button
                key={project.id}
                onClick={() => handleProjectSelect(project.id)}
                className="w-full flex items-center px-2 py-2 text-sm text-left text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md"
              >
                <FolderOpen className="w-4 h-4 mr-2 text-gray-400 dark:text-gray-500" />
                <span className="flex-1 truncate">{project.name}</span>
                {project.id === selectedProject?.id && (
                  <div className="w-2 h-2 bg-blue-600 dark:bg-blue-400 rounded-full" />
                )}
              </button>
            ))}
            <div className="border-t border-gray-200 dark:border-gray-700 mt-2 pt-2">
              <button 
                className="w-full flex items-center px-2 py-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md"
                onClick={handleCreateProject}
              >
                <Plus className="w-4 h-4 mr-2" />
                Create new project
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}